import React, { useState } from "react";
import { Save, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

/** Field list mirrors the Excel column set the user can edit.
 *  Order = the order they appear in the modal.
 */
const EDITABLE_FIELDS = [
  "source_id",
  "question_text",
  "option_a",
  "option_b",
  "option_c",
  "option_d",
  "correct_answer",
  "rationale",
  "exam",
  "subject",
  "category",
  "bloom_level",
  "question_type",
  "difficulty",
  "time",
  "mark",
];

const NUMERIC_FIELDS = new Set(["time", "mark"]);

export default function EditQuestionModal({ question, onClose, onSaved }) {
  const [form, setForm] = useState({ ...question });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      for (const k of EDITABLE_FIELDS) {
        const raw = form[k];
        if (raw === "" || raw == null) continue;
        if (NUMERIC_FIELDS.has(k)) {
          const n = Number(raw);
          if (!Number.isNaN(n)) payload[k] = n;
        } else {
          payload[k] = String(raw);
        }
      }
      await api.patch(`/questions/${question.id}`, payload);
      toast.success("Question updated");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        data-testid="edit-question-modal"
        className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full p-6 my-8 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
            Edit Question{" "}
            <span className="text-slate-400 font-mono text-sm">#{question.source_id || "—"}</span>
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <TextField
              k="source_id"
              label="Source ID"
              value={form.source_id}
              onChange={set}
            />
            <TextField
              k="exam"
              label="Exam"
              value={form.exam}
              onChange={set}
            />
            <TextField
              k="question_type"
              label="Question Type"
              value={form.question_type}
              onChange={set}
            />
          </div>

          <TextField
            k="question_text"
            label="Question Text"
            value={form.question_text}
            onChange={set}
            type="textarea"
            rows={3}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <TextField k="option_a" label="Option A" value={form.option_a} onChange={set} />
            <TextField k="option_b" label="Option B" value={form.option_b} onChange={set} />
            <TextField k="option_c" label="Option C" value={form.option_c} onChange={set} />
            <TextField k="option_d" label="Option D" value={form.option_d} onChange={set} />
          </div>

          <TextField
            k="correct_answer"
            label="Correct Answer"
            value={form.correct_answer}
            onChange={set}
          />

          <TextField
            k="rationale"
            label="Rationale"
            value={form.rationale}
            onChange={set}
            type="textarea"
            rows={3}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <TextField k="subject" label="Subject" value={form.subject} onChange={set} />
            <TextField k="category" label="Category" value={form.category} onChange={set} />
            <TextField
              k="bloom_level"
              label="Bloom Level"
              value={form.bloom_level}
              onChange={set}
            />
            <TextField
              k="difficulty"
              label="Difficulty"
              value={form.difficulty}
              onChange={set}
            />
            <TextField
              k="time"
              label="Time (sec)"
              value={form.time}
              onChange={set}
              numeric
            />
            <TextField k="mark" label="Mark" value={form.mark} onChange={set} numeric />
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={onClose}
            data-testid="edit-modal-cancel"
            className="px-4 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            data-testid="edit-modal-save"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {busy ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

function TextField({ k, label, value, onChange, type = "input", rows = 2, numeric = false }) {
  return (
    <div>
      <label className="block text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
        {label}
      </label>
      {type === "textarea" ? (
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(k, e.target.value)}
          rows={rows}
          data-testid={`edit-field-${k}`}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
        />
      ) : (
        <input
          type={numeric ? "number" : "text"}
          step={numeric ? "0.5" : undefined}
          value={value ?? ""}
          onChange={(e) => onChange(k, e.target.value)}
          data-testid={`edit-field-${k}`}
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
        />
      )}
    </div>
  );
}
